import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LanguageService } from '../../../core/i18n/language.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppMaterialModule } from '../../../app-material.module';
import { BreakpointObserver } from '@angular/cdk/layout';

export type LangId = 'pt-BR' | 'en-US' | 'es-ES';

const LANG_OPTIONS: { id: LangId; label: string; shortLabel: string; flagUrl: string }[] = [
    {
        id: 'pt-BR',
        label: 'Português',
        shortLabel: 'PT',
        flagUrl: 'https://flagcdn.com/w40/br.png',
    },
    {
        id: 'en-US',
        label: 'English',
        shortLabel: 'EN',
        flagUrl: 'https://flagcdn.com/w40/us.png',
    },
    {
        id: 'es-ES',
        label: 'Español',
        shortLabel: 'ES',
        flagUrl: 'https://flagcdn.com/w40/es.png',
    },
];

@Component({
    selector: 'app-language-toggle',
    standalone: true,
    imports: [CommonModule, TranslocoPipe, AppMaterialModule],
    providers: [...provideTranslocoScope('shared')],
    templateUrl: './language-toggle.component.html',
    styleUrl: './language-toggle.component.scss',
})
export class LanguageToggleComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly mobileQuery = '(max-width: 767.98px)';

    public mode: 'inline' | 'dropdown' = 'inline';
    public readonly langOptions = LANG_OPTIONS;
    public activeLang: string = 'pt-BR';

    constructor(
        public languageService: LanguageService,
        private transloco: TranslocoService,
        private breakpointObserver: BreakpointObserver,
    ) {}

    public ngOnInit(): void {
        this.activeLang = this.languageService.getActive();
        this.subscribeToViewportChanges();
        this.subscribeToLanguageChanges();
    }

    private subscribeToViewportChanges(): void {
        this.breakpointObserver
            .observe(this.mobileQuery)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ matches }) => {
                this.mode = matches ? 'dropdown' : 'inline';
            });
    }

    private subscribeToLanguageChanges(): void {
        this.transloco.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((lang) => {
            this.activeLang = lang;
        });
    }

    public isActive(langId: LangId): boolean {
        return this.activeLang === langId;
    }

    public setLang(lang: LangId): void {
        this.activeLang = lang;
        this.languageService.set(lang);
    }
}
